import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, itemVariants, subtleItemVariants } from './dashboardMotion';
import { database, storage } from '../firebaseConfig';
import { ref, onValue, update, set, remove, serverTimestamp, runTransaction } from 'firebase/database';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  billingPeriods,
  nextDuePeriod,
  contractRunning,
  payoutRemainingMonths,
  memberMonthlyPayout,
  payoutForPaidInvoice,
  paymentId,
  payoutId,
  reportingId,
  fmtEur,
  fmtDate,
  monthLabel,
  grossFromNet,
  appointmentPriceFor,
  appointmentsOnPayment,
  includedAppointmentsFor,
  commissionActive,
  commissionForMember,
  commissionRoleInfo,
  customerRoleOverride,
  oneTimePeriods,
  oneTimeIdFromPeriodKey,
  findOneTime,
  payoutForPaidOneTime,
} from './paymentEngine';
import payoutStatement from './payoutStatementCore';
import { computeProjection } from '../Research/pricingEngine';
import { buildExtraApptUpdates } from './reportingSync';
import { buildOneTimeSyncUpdates } from './oneTimeSync';
import ReportingDashboard from './ReportingDashboard';
import './PaymentDashboards.css';

const CUSTOMERS_PATH = 'salesgod-research/customers';
const REPORTS_PATH = 'salesgod-research/reports';
const TEAM_PATH = 'salesgod-research/team';
const PAYMENTS_PATH = 'salesgod-research/payments';
const PAYOUTS_PATH = 'salesgod-research/payouts';
const PAYOUT_INVOICE_STORAGE = 'salesgod-research/payout-invoices';
const CUSTOMER_INVOICE_STORAGE = 'salesgod-research/customer-invoices';
const MAX_INVOICE_BYTES = 25 * 1024 * 1024;
const INVOICE_COUNTER_PATH = 'salesgod-research/invoiceCounter';
const CUSTOMER_COUNTER_PATH = 'salesgod-research/customerCounter';
const TEAM_COUNTER_PATH = 'salesgod-research/teamCounter';
const FIRST_INVOICE_NO = 2601;
const FIRST_CUSTOMER_NO = 1001;
const FIRST_MEMBER_NO = 1001;

// Reserviert atomar `count` fortlaufende Nummern aus einem globalen Zähler und
// liefert die erste. Nummern werden nie wiederverwendet.
const reserveNumbers = async (path, first, count) => {
  if (count <= 0) return 0;
  const res = await runTransaction(ref(database, path), (cur) => {
    const c = typeof cur === 'number' && cur >= first ? cur : first;
    return c + count;
  });
  return res.snapshot.val() - count;
};
// Rechnungsnummern (Start 2601) – auch eine stornierte Rechnung behält ihre Nummer.
const reserveInvoiceNumbers = (count) => reserveNumbers(INVOICE_COUNTER_PATH, FIRST_INVOICE_NO, count);
// Kunden-IDs (Start 1001) – eindeutiger, stabiler Identifier je Unternehmen für die spätere API.
const reserveCustomerNumbers = (count) => reserveNumbers(CUSTOMER_COUNTER_PATH, FIRST_CUSTOMER_NO, count);
// Mitarbeiter-IDs (Start 1001) – eindeutiger, stabiler Identifier je Teammitglied für die spätere API.
const reserveTeamNumbers = (count) => reserveNumbers(TEAM_COUNTER_PATH, FIRST_MEMBER_NO, count);

const fmtInvoiceNo = (n) => (typeof n === 'number' ? `RE-${n}` : null);
const fmtCustomerId = (n) => (typeof n === 'number' ? `KD-${n}` : null);
const fmtMemberNo = (n) => (typeof n === 'number' ? `MA-${n}` : null);

const todayInput = () => new Date().toISOString().slice(0, 10);
const dateInputToMs = (s) => (s ? new Date(s + 'T09:00:00').getTime() : Date.now());
const sourceLabel = (src) => (src === 'manual' ? 'Manuell' : src === 'cron' || src === 'auto' ? 'Auto' : null);

const FILTERS = [
  { id: 'open', label: 'Offen' },
  { id: 'invoiced', label: 'Rechnung gestellt' },
  { id: 'paid', label: 'Bezahlt' },
  { id: 'noinvoice', label: 'Beleg fehlt' },
  { id: 'cancelled', label: 'Storniert' },
  { id: 'all', label: 'Alle' },
];

const OUT_FILTERS = [
  { id: 'open', label: 'Offen' },
  { id: 'paid', label: 'Bezahlt' },
  { id: 'noinvoice', label: 'Rechnung fehlt' },
  { id: 'cancelled', label: 'Storniert' },
  { id: 'all', label: 'Alle' },
];

// Belegdokument einer Zahlung: bei Auszahlungen die Rechnung, die der Freelancer
// stellt, bei Eingängen die Rechnung, die wir dem Kunden geschickt haben.
// `invoiceUrl` ist der Altbestand der Eingänge (früher nur ein Link).
const invoiceDoc = (rec) => {
  if (rec && rec.invoice && rec.invoice.url) return rec.invoice;
  if (rec && rec.invoiceUrl) return { url: rec.invoiceUrl };
  return null;
};
// Beleg erwartet, aber nicht vorhanden? Bei Eingängen ab Rechnungsstellung,
// bei Auszahlungen ab der Zahlung.
const invoiceMissing = (rec) => {
  if (!rec || rec.status === 'cancelled' || invoiceDoc(rec)) return false;
  return rec.status === 'paid' || rec.status === 'invoiced';
};
const fmtFileSize = (bytes) => {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(b < 10 * 1024 * 1024 ? 1 : 0)} MB`;
};

// Storno-Schnellgründe je Kontext (Pflicht-Grund, frei ergänzbar).
const CANCEL_REASONS = {
  payment: ['Kunde hat nicht gezahlt', 'Rechnung erlassen', 'Auf Kulanz storniert', 'Doppelt erfasst', 'Falscher Betrag'],
  future: ['Kunde pausiert', 'Vertrag beendet', 'Zahlungsausfall', 'Auf Kulanz pausiert'],
  payout: ['Mitarbeiter ausgeschieden', 'Auf Kulanz storniert', 'Doppelt erfasst', 'Falscher Betrag'],
};

const sameMonth = (ms, refMs) => {
  if (!ms) return false;
  const a = new Date(ms); const b = new Date(refMs);
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
};

// Termin-Anzahl direkt eintragen (Monatsabschluss). Lokaler Puffer, damit beim
// Tippen nicht jede Zwischenzahl in die Abrechnung geschrieben wird – committet
// wird bei Enter oder Verlassen des Feldes.
const ApptCountInput = ({ value, label, onCommit }) => {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  const commit = () => {
    const next = draft.trim() === '' ? 0 : Math.max(0, Math.round(Number(draft)) || 0);
    if (next !== value) onCommit(next); else setDraft(String(value));
  };
  return (
    <input
      type="text"
      inputMode="numeric"
      className="pay-appts-input"
      value={draft}
      aria-label={`${label} eintragen`}
      onFocus={(e) => e.target.select()}
      onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ''))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') { setDraft(String(value)); e.currentTarget.blur(); }
      }}
    />
  );
};

const payoutEndDate = (member, contract) => {
  const dur = Number(member?.durationMonths) || 0;
  if (dur <= 0 || !contract?.startAt) return null;
  const s = new Date(contract.startAt);
  return new Date(s.getFullYear(), s.getMonth() + dur, s.getDate()).getTime();
};

const FinanceDashboard = ({ initialView = 'in' } = {}) => {
  const [customers, setCustomers] = useState({});
  const [team, setTeam] = useState({});
  const [payments, setPayments] = useState({});
  const [payouts, setPayouts] = useState({});
  const [reports, setReports] = useState({});
  const [reportings, setReportings] = useState({});
  const [loadingC, setLoadingC] = useState(true);
  const [loadingT, setLoadingT] = useState(true);
  const [loadingP, setLoadingP] = useState(true);
  const [loadingPo, setLoadingPo] = useState(true);
  const [view, setView] = useState(initialView === 'reporting' || initialView === 'out' ? initialView : 'in'); // 'in' = Eingänge | 'out' = Auszahlungen | 'reporting' = Reportings
  const [filter, setFilter] = useState('open');
  const [outFilter, setOutFilter] = useState('open');
  const [now, setNow] = useState(Date.now());
  const [modal, setModal] = useState(null); // null | 'payment' | 'payout'
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [menuId, setMenuId] = useState(null); // offenes 3-Punkte-Menü (Row-ID)
  const [cancelModal, setCancelModal] = useState(null); // { kind: 'payment'|'payout', scope: 'single'|'future', item }
  const [cancelReason, setCancelReason] = useState('');
  // Zahlung bestätigen / Rechnung dokumentieren
  const [invModal, setInvModal] = useState(null); // { kind: 'payment'|'payout', item, mode }
  const [invFile, setInvFile] = useState(null);   // hochgeladene Datei (noch nicht bestätigt)
  const [invLink, setInvLink] = useState('');
  const [invNo, setInvNo] = useState('');
  const [invBusy, setInvBusy] = useState(false);
  const [invPct, setInvPct] = useState(0);
  const [invError, setInvError] = useState('');
  const invInputRef = useRef(null);
  const genRef = useRef(false);
  const genPayoutRef = useRef(false);
  const reconcileRef = useRef(false);
  const oneTimeSyncRef = useRef(false);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  // Offenes 3-Punkte-Menü bei Klick irgendwo schließen.
  useEffect(() => {
    if (!menuId) return undefined;
    const close = () => setMenuId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuId]);
  useEffect(() => {
    const unsub = onValue(ref(database, CUSTOMERS_PATH), (snap) => { setCustomers(snap.val() || {}); setLoadingC(false); });
    return () => unsub();
  }, []);
  useEffect(() => {
    const unsub = onValue(ref(database, TEAM_PATH), (snap) => { setTeam(snap.val() || {}); setLoadingT(false); });
    return () => unsub();
  }, []);
  useEffect(() => {
    const unsub = onValue(ref(database, PAYMENTS_PATH), (snap) => { setPayments(snap.val() || {}); setLoadingP(false); });
    return () => unsub();
  }, []);
  useEffect(() => {
    const unsub = onValue(ref(database, PAYOUTS_PATH), (snap) => { setPayouts(snap.val() || {}); setLoadingPo(false); });
    return () => unsub();
  }, []);
  // Research-Reports nur lesend – liefern den Termin-Preis (appointmentValue) je Kunde.
  useEffect(() => {
    const unsub = onValue(ref(database, REPORTS_PATH), (snap) => setReports(snap.val() || {}));
    return () => unsub();
  }, []);
  // Monatliche Reportings – für die Kopplung Extra-Termine ⇄ Reporting.
  useEffect(() => {
    const unsub = onValue(ref(database, 'salesgod-research/reportings'), (snap) => setReportings(snap.val() || {}));
    return () => unsub();
  }, []);

  // Zentraler Generator (Frontend) – idempotent:
  //  • vergibt jedem Vertragskunden eine eindeutige, stabile Kunden-ID (KD-1001…)
  //    als unikten Identifier für die spätere Rechnungs-API,
  //  • erzeugt fehlende, fällige Rechnungen,
  //  • vergibt fortlaufende Rechnungsnummern (RE-2601…), chronologisch sortiert,
  //  • stempelt die Kunden-ID auf jede Rechnung (auch nachträglich bei Altbestand).
  useEffect(() => {
    if (loadingC || loadingP || genRef.current) return;

    // Vertragskunden ohne Kunden-ID -> brauchen eine (chronologisch nach Start).
    const custNeedId = Object.keys(customers)
      .filter((slug) => customers[slug] && customers[slug].contract && typeof customers[slug].customerId !== 'number')
      .sort((a, b) => {
        const sa = (customers[a].contract && customers[a].contract.startAt) || customers[a].createdAt || 0;
        const sb = (customers[b].contract && customers[b].contract.startAt) || customers[b].createdAt || 0;
        return sa - sb || a.localeCompare(b);
      });

    // Neue, fällige Rechnungen: Vertrag (Setup + monatlich) und zusätzlich die
    // am Kunden hinterlegten Einmalzahlungen (Setup-Fee, Beratung, …).
    const toCreate = [];
    Object.keys(customers).forEach((slug) => {
      const c = customers[slug] || {};
      const due = [...billingPeriods(c.contract, now), ...oneTimePeriods(c, now)];
      due.forEach((p) => {
        const id = paymentId(slug, p.periodKey);
        if (!payments[id]) toCreate.push({ id, slug, dueDate: p.dueDate, p, c });
      });
    });

    // Bestehende Rechnungen ohne Rechnungsnummer (Altbestand).
    const toNumber = Object.keys(payments)
      .filter((id) => typeof payments[id].invoiceNo !== 'number')
      .map((id) => ({ id, dueDate: payments[id].dueDate || 0 }));

    // Bestehende Rechnungen ohne Kunden-ID, deren Kunde eine hat oder bekommt.
    const willHaveId = (slug) => customers[slug] && (typeof customers[slug].customerId === 'number' || !!customers[slug].contract);
    const payMissingCustId = Object.keys(payments)
      .filter((id) => typeof payments[id].customerId !== 'number' && payments[id].customerSlug && willHaveId(payments[id].customerSlug))
      .map((id) => ({ id, slug: payments[id].customerSlug }));

    if (!custNeedId.length && !toCreate.length && !toNumber.length && !payMissingCustId.length) return;

    genRef.current = true;
    (async () => {
      try {
        // Kunden-IDs reservieren + lokale Auflösungs-Map aufbauen.
        const idMap = {};
        Object.keys(customers).forEach((slug) => {
          if (typeof customers[slug].customerId === 'number') idMap[slug] = customers[slug].customerId;
        });
        if (custNeedId.length) {
          const cStart = await reserveCustomerNumbers(custNeedId.length);
          custNeedId.forEach((slug, i) => { idMap[slug] = cStart + i; });
        }

        // Rechnungsnummern für neue + unnummerierte Rechnungen reservieren.
        const ordered = [
          ...toCreate.map((x) => ({ ...x, isNew: true })),
          ...toNumber.map((x) => ({ ...x, isNew: false })),
        ].sort((a, b) => (a.dueDate || 0) - (b.dueDate || 0) || a.id.localeCompare(b.id));
        const iStart = ordered.length ? await reserveInvoiceNumbers(ordered.length) : 0;

        const updates = {};
        // Kunden-IDs schreiben.
        custNeedId.forEach((slug) => { updates[`${CUSTOMERS_PATH}/${slug}/customerId`] = idMap[slug]; });
        // Rechnungen erzeugen / nummerieren.
        ordered.forEach((item, i) => {
          const no = iStart + i;
          if (item.isNew) {
            const cid = idMap[item.slug];
            const rec = {
              customerSlug: item.slug,
              companyName: item.c.companyName || item.slug,
              type: item.p.type,
              periodKey: item.p.periodKey,
              dueDate: item.p.dueDate,
              amount: item.p.amount,
              net: item.p.net,
              vat: item.p.vat,
              vatRate: item.p.vatRate,
              taxMode: item.p.taxMode,
              invoiceNo: no,
              status: 'due',
              createdAt: serverTimestamp(),
              source: 'auto',
            };
            // Einmalzahlungen tragen ihre Bezeichnung mit auf die Rechnung –
            // „Beratung Q1" statt eines nichtssagenden Periodenschlüssels.
            if (item.p.oneTimeId) {
              rec.oneTimeId = item.p.oneTimeId;
              rec.label = item.p.label;
            }
            if (typeof cid === 'number') rec.customerId = cid;
            updates[`${PAYMENTS_PATH}/${item.id}`] = rec;
          } else {
            updates[`${PAYMENTS_PATH}/${item.id}/invoiceNo`] = no;
            const cid = idMap[payments[item.id].customerSlug];
            if (typeof payments[item.id].customerId !== 'number' && typeof cid === 'number') {
              updates[`${PAYMENTS_PATH}/${item.id}/customerId`] = cid;
            }
          }
        });
        // Kunden-ID auf restliche Altbestand-Rechnungen nachstempeln.
        payMissingCustId.forEach(({ id, slug }) => {
          if (updates[`${PAYMENTS_PATH}/${id}`] || updates[`${PAYMENTS_PATH}/${id}/customerId`] != null) return;
          const cid = idMap[slug];
          if (typeof cid === 'number') updates[`${PAYMENTS_PATH}/${id}/customerId`] = cid;
        });

        if (Object.keys(updates).length) await update(ref(database), updates);
      } catch (e) {
        console.error('Zahlungen/Kunden-IDs erzeugen fehlgeschlagen:', e);
      } finally {
        genRef.current = false;
      }
    })();
  }, [loadingC, loadingP, customers, payments, now]);

  // Zentraler Auszahlungs-Generator (Frontend) – ZAHLUNGSGESTEUERT:
  // Eine Team-Auszahlung entsteht erst, wenn der Kunde die zugehörige
  // Monatsrechnung BEZAHLT hat (status 'paid'). Das spiegelt die echte
  // Reihenfolge: Rechnung fällig → gestellt → Kunde zahlt → dadurch werden die
  // Provisionen/Fixvergütungen der im Auftrag beteiligten (= dem Kunden
  // zugewiesenen) Mitglieder fällig und erscheinen als offene Auszahlungsposten.
  //  • vergibt jedem Teammitglied eine eindeutige, stabile Mitarbeiter-ID (MA-1001…),
  //  • erzeugt je bezahlte Rechnung × zugewiesenem Mitglied eine Auszahlung
  //    (Provision/Fix, inkl. Bonus für verrechnete Extra-Termine) mit Status 'open',
  //  • keine rückwirkenden Kosten: Rechnungen, die vor der Zuweisung bezahlt wurden,
  //    lösen für das Mitglied nichts aus,
  //  • stempelt die Mitarbeiter-ID auf jede Auszahlung (auch nachträglich).
  useEffect(() => {
    if (loadingC || loadingT || loadingPo || loadingP || genPayoutRef.current) return;

    // Teammitglieder ohne Mitarbeiter-ID -> brauchen eine (chronologisch nach Anlage).
    const memberNeedId = Object.keys(team)
      .filter((mid) => typeof team[mid].memberNo !== 'number')
      .sort((a, b) => (team[a].createdAt || 0) - (team[b].createdAt || 0) || a.localeCompare(b));

    // Neue Auszahlungen aus BEZAHLTEN Kundenrechnungen ableiten – aus den
    // wiederkehrenden Monatsrechnungen (alle zugewiesenen Mitglieder) und aus
    // bezahlten Einmalzahlungen (nur die Mitglieder, für die dort ausdrücklich
    // ein Anteil hinterlegt wurde).
    const toCreate = [];
    Object.keys(payments).forEach((pid) => {
      const pay = payments[pid];
      if (!pay || pay.status !== 'paid') return;
      const slug = pay.customerSlug;
      if (!slug || !pay.periodKey || pid !== paymentId(slug, pay.periodKey)) return;
      const c = customers[slug];
      if (!c) return;

      if (pay.type === 'onetime') {
        const ot = findOneTime(c, pay.oneTimeId || oneTimeIdFromPeriodKey(pay.periodKey));
        if (!ot) return;
        Object.keys(ot.commissions).forEach((memberId) => {
          const m = team[memberId];
          if (!m) return;
          const po = payoutForPaidOneTime(m, ot, pay, memberId);
          if (!po) return;
          const id = payoutId(memberId, slug, po.periodKey);
          if (!payouts[id]) toCreate.push({ id, memberId, slug, c, m, po, kind: 'onetime' });
        });
        return;
      }

      if (pay.type !== 'recurring' || !c.contract) return;
      const assigned = c.team || {};
      Object.keys(assigned).forEach((memberId) => {
        const m = team[memberId];
        if (!m) return;
        const assignedAt = typeof assigned[memberId] === 'number' ? assigned[memberId] : 0;
        const po = payoutForPaidInvoice(m, c.contract, pay, assignedAt, customerRoleOverride(c, memberId));
        if (!po) return;
        const id = payoutId(memberId, slug, po.periodKey);
        if (!payouts[id]) toCreate.push({ id, memberId, slug, c, m, po, kind: 'recurring' });
      });
    });

    // Bestehende Auszahlungen ohne Mitarbeiter-ID, deren Mitglied noch existiert.
    const poMissingNo = Object.keys(payouts)
      .filter((id) => typeof payouts[id].memberNo !== 'number' && payouts[id].memberId && team[payouts[id].memberId])
      .map((id) => ({ id, memberId: payouts[id].memberId }));

    if (!memberNeedId.length && !toCreate.length && !poMissingNo.length) return;

    genPayoutRef.current = true;
    (async () => {
      try {
        // Mitarbeiter-IDs reservieren + lokale Auflösungs-Map aufbauen.
        const noMap = {};
        Object.keys(team).forEach((mid) => { if (typeof team[mid].memberNo === 'number') noMap[mid] = team[mid].memberNo; });
        if (memberNeedId.length) {
          const mStart = await reserveTeamNumbers(memberNeedId.length);
          memberNeedId.forEach((mid, i) => { noMap[mid] = mStart + i; });
        }

        const updates = {};
        // Mitarbeiter-IDs schreiben.
        memberNeedId.forEach((mid) => { updates[`${TEAM_PATH}/${mid}/memberNo`] = noMap[mid]; });
        // Auszahlungen erzeugen.
        toCreate.forEach((item) => {
          const rec = {
            memberId: item.memberId,
            memberName: item.m.name || item.memberId,
            customerSlug: item.slug,
            companyName: item.c.companyName || item.slug,
            type: item.kind === 'onetime' ? 'onetime' : 'recurring',
            payType: item.po.payType,
            periodKey: item.po.periodKey,
            amount: item.po.amount,
            baseAmount: item.po.baseAmount,
            date: item.po.dueDate,
            status: 'open',
            createdAt: serverTimestamp(),
            source: 'auto',
          };
          if (item.kind === 'onetime') {
            // Prozentsatz bzw. Fixbetrag und die Bezugsgröße mitschreiben, damit
            // die Auszahlung später ohne Blick in den Kundendatensatz erklärbar ist.
            rec.oneTimeId = item.po.oneTimeId;
            rec.label = item.po.label;
            rec.commissionMode = item.po.commissionMode;
            rec.commissionValue = item.po.commissionValue;
            rec.commissionBase = item.po.commissionBase;
            rec.commissionAmount = item.po.amount;
          } else {
            rec.appointmentBonus = item.po.appointmentBonus;
            rec.extraAppointments = item.po.extraAppointments;
            rec.appointments = item.po.appointments;
            // Gestaffelte Termin-Provision mitschreiben – macht die Auszahlung
            // im Dashboard nachvollziehbar (Rolle, Betrag, Provisionsbasis).
            if (item.po.commissionRole) {
              rec.commissionRole = item.po.commissionRole;
              rec.commissionAmount = item.po.commissionAmount;
              rec.commissionBase = item.po.commissionBase;
              rec.commissionMinimumApplied = !!item.po.commissionMinimumApplied;
            }
          }
          if (typeof noMap[item.memberId] === 'number') rec.memberNo = noMap[item.memberId];
          updates[`${PAYOUTS_PATH}/${item.id}`] = rec;
        });
        // Mitarbeiter-ID auf Altbestand-Auszahlungen nachstempeln.
        poMissingNo.forEach(({ id, memberId }) => {
          if (updates[`${PAYOUTS_PATH}/${id}`]) return;
          const no = noMap[memberId];
          if (typeof no === 'number') updates[`${PAYOUTS_PATH}/${id}/memberNo`] = no;
        });

        if (Object.keys(updates).length) await update(ref(database), updates);
      } catch (e) {
        console.error('Auszahlungen/Mitarbeiter-IDs erzeugen fehlgeschlagen:', e);
      } finally {
        genPayoutRef.current = false;
      }
    })();
  }, [loadingC, loadingT, loadingPo, loadingP, customers, team, payouts, payments]);

  // Selbstheilende Kopplung: jede Rechnung wird mit ihrem bereits eingereichten
  // Reporting abgeglichen. Schließt die Timing-Lücke, falls ein Report
  // eingereicht wurde, bevor die zugehörige Rechnung existierte (oder vom Cron
  // ohne Extra-Termine erzeugt wurde).
  //
  // Zwei Fälle, weil ein Reporting auch NACH der Rechnungsstellung eintreffen
  // kann (der Outreach-Manager reicht es über die Research-Seite ein):
  //   • Rechnung offen -> Beträge + Reporting + Auszahlungen angleichen
  //   • Rechnung raus  -> Beträge bleiben unangetastet, aber die Terminzahl und
  //     damit die Team-Provision müssen trotzdem ankommen
  // Der zweite Fall gilt nur für Provisionsverträge: dort hängt die komplette
  // Vergütung an der Terminzahl. Bei klassischen Verträgen bleibt es beim
  // bisherigen Verhalten, damit der Abgleich nicht rückwirkend über den
  // gesamten Rechnungsbestand läuft.
  // Idempotent: schreibt nur bei echter Abweichung und konvergiert.
  useEffect(() => {
    if (loadingC || loadingP || loadingPo || reconcileRef.current) return;
    const updates = {};
    Object.keys(payments).forEach((id) => {
      const p = payments[id];
      if (!p || p.status === 'cancelled' || !p.periodKey || !p.customerSlug) return;
      if (id !== paymentId(p.customerSlug, p.periodKey)) return;
      const rep = reportings[reportingId(p.customerSlug, p.periodKey)];
      if (!rep || rep.status !== 'submitted') return;
      const cont = (customers[p.customerSlug] || {}).contract || null;
      if (p.status !== 'due' && !commissionActive(cont)) return;
      const targetExtra = Number(rep.extraAppointments) || 0;
      const targetAppts = typeof rep.qualifiedAppointments === 'number'
        ? rep.qualifiedAppointments
        : targetExtra + includedAppointmentsFor(cont);
      const amountsStale = p.status === 'due' && targetExtra !== (Number(p.extraAppointments) || 0);
      const apptsStale = p.appointments !== targetAppts;
      if (!amountsStale && !apptsStale) return;
      // Beträge erst angleichen, wenn ein Termin-Preis auflösbar ist (Research
      // geladen) – sonst würde ein 0er-Preis eingebrannt.
      const price = Number(p.appointmentPrice) > 0 ? Number(p.appointmentPrice) : apptPrice(p.customerSlug);
      if (amountsStale && targetExtra > 0 && price <= 0) return;
      Object.assign(updates, buildExtraApptUpdates({
        slug: p.customerSlug, periodKey: p.periodKey, extra: targetExtra, qualified: targetAppts,
        customers, team, payments, payouts, reportings, apptPrice,
      }));
    });
    if (!Object.keys(updates).length) return;
    reconcileRef.current = true;
    update(ref(database), updates)
      .catch((e) => console.error('Reporting-Sync (Rechnungen/Auszahlungen) fehlgeschlagen:', e))
      .finally(() => { reconcileRef.current = false; });
  }, [loadingC, loadingP, loadingPo, payments, reportings, customers, team, payouts, reports]);

  // Einmalzahlungen konsistent halten: Definition (Pipeline) ↔ Rechnung ↔
  // Auszahlungen. Die Regeln stecken vollständig in buildOneTimeSyncUpdates –
  // hier bleibt nur das Anstoßen und Schreiben.
  useEffect(() => {
    if (loadingC || loadingP || loadingPo || oneTimeSyncRef.current) return;
    const updates = buildOneTimeSyncUpdates({ customers, payments, payouts });
    if (!Object.keys(updates).length) return;
    oneTimeSyncRef.current = true;
    update(ref(database), updates)
      .catch((e) => console.error('Einmalzahlungen-Sync fehlgeschlagen:', e))
      .finally(() => { oneTimeSyncRef.current = false; });
  }, [loadingC, loadingP, loadingPo, customers, payments, payouts]);

  // ── Eingänge ──
  const list = useMemo(() =>
    Object.keys(payments).map((id) => ({ id, ...payments[id] })).sort((a, b) => (b.dueDate || 0) - (a.dueDate || 0)),
  [payments]);

  const counts = useMemo(() => ({
    open: list.filter((p) => p.status !== 'invoiced' && p.status !== 'paid' && p.status !== 'cancelled').length,
    invoiced: list.filter((p) => p.status === 'invoiced').length,
    paid: list.filter((p) => p.status === 'paid').length,
    // Rechnung ist raus, aber das Dokument liegt nicht bei uns im System.
    noinvoice: list.filter(invoiceMissing).length,
    cancelled: list.filter((p) => p.status === 'cancelled').length,
    all: list.length,
  }), [list]);

  const filtered = useMemo(() => {
    if (filter === 'all') return list;
    if (filter === 'open') return list.filter((p) => p.status !== 'invoiced' && p.status !== 'paid' && p.status !== 'cancelled');
    if (filter === 'noinvoice') return list.filter(invoiceMissing);
    return list.filter((p) => p.status === filter);
  }, [list, filter]);

  // Provisionsvorschau je Rechnung. Beim Provisionsmodell hängt alles an der
  // Terminzahl: sie bestimmt den Rechnungsbetrag UND was das Team bekommt.
  // Solange die Rechnung nicht bezahlt ist, existiert noch kein Auszahlungs-
  // Datensatz – hier wird deshalb vorgerechnet, was fällig WIRD ("vorgemerkt").
  //   needsAppts – Provisionsmodell aktiv, aber noch keine Termine erfasst
  const commissionPreview = useMemo(() => {
    const cache = {};
    return (p) => {
      if (cache[p.id]) return cache[p.id];
      const c = (p.customerSlug && customers[p.customerSlug]) || null;
      const contract = c && c.contract;
      const empty = { active: false, needsAppts: false, appointments: 0, shares: [], total: 0 };
      if (!c || !commissionActive(contract) || p.type !== 'recurring' || p.status === 'cancelled') {
        cache[p.id] = empty;
        return empty;
      }
      const recorded = typeof p.appointments === 'number' || typeof p.extraAppointments === 'number';
      const appointments = appointmentsOnPayment(p, contract);
      const shares = [];
      Object.keys(c.team || {}).forEach((memberId) => {
        const m = team[memberId];
        if (!m) return;
        const share = commissionForMember(m, contract, appointments, customerRoleOverride(c, memberId));
        if (!share) return;
        shares.push({
          memberId,
          name: m.name || memberId,
          roleLabel: (commissionRoleInfo(share.roleId) || {}).label || '',
          amount: share.amount,
        });
      });
      const out = {
        active: true,
        needsAppts: !recorded,
        appointments,
        shares,
        total: shares.reduce((s, x) => s + x.amount, 0),
      };
      cache[p.id] = out;
      return out;
    };
    // `payments` gehört in die Abhängigkeiten, obwohl die Funktion es nicht
    // direkt liest: sonst überlebt der Cache das Eintragen der Termine und
    // zeigt weiter die alte Provision.
  }, [customers, team, payments]);

  // Rechnungen, bei denen der Admin die Termine noch eintragen muss. Ohne sie
  // geht die Rechnung über 0 € raus und das Team bekommt keine Provision.
  const apptTodo = useMemo(
    () => list.filter((p) => p.status !== 'paid' && p.status !== 'cancelled' && commissionPreview(p).needsAppts),
    [list, commissionPreview],
  );

  // Wer bekommt noch was? Je Mitarbeiter: die bereits freigeschalteten
  // Auszahlungen (Kunde hat gezahlt) plus die voraussichtlichen aus noch
  // offenen Kundenrechnungen. Gerechnet im payoutStatementCore – exakt dieselbe
  // Grundlage wie die teilbare Auszahlungsseite jedes Mitarbeiters.
  const memberSummary = useMemo(
    () => payoutStatement
      .payoutSummaryByMember({ customers, team, payments, payouts, now })
      .filter((m) => m.outstanding > 0 || m.openCount > 0 || m.expectedCount > 0),
    [customers, team, payments, payouts, now],
  );

  // Vorschau auf das, was noch kommt: die nächste Monatsrechnung je aktivem
  // Vertrag plus alle geplanten Einmalzahlungen, die noch nicht fällig sind.
  const upcoming = useMemo(() => {
    const out = [];
    Object.keys(customers).forEach((slug) => {
      const c = customers[slug] || {};
      const companyName = c.companyName || slug;
      const nd = c.contract && c.contract.active ? nextDuePeriod(c.contract, now) : null;
      if (nd) out.push({ key: `${slug}__${nd.periodKey}`, slug, companyName, title: monthLabel(nd.periodKey), ...nd });
      oneTimePeriods(c, Number.MAX_SAFE_INTEGER).forEach((e) => {
        if (e.dueDate <= now) return; // schon fällig -> steht bereits als Rechnung in der Liste
        out.push({ key: `${slug}__${e.periodKey}`, slug, companyName, title: e.label, ...e });
      });
    });
    return out.sort((a, b) => a.dueDate - b.dueDate).slice(0, 8);
  }, [customers, now]);

  // ── Auszahlungen (abgeleitet) ──
  const members = useMemo(() => {
    return Object.keys(team).map((id) => {
      const m = { id, ...team[id] };
      const custs = [];
      Object.keys(customers).forEach((slug) => {
        const c = customers[slug] || {};
        if (!(c.team && c.team[id] != null)) return;
        const contract = c.contract || null;
        const active = !!(contract && contract.active && contractRunning(contract, now));
        const remaining = payoutRemainingMonths(m, contract, now);
        const payActive = active && remaining > 0;
        const payout = memberMonthlyPayout(m, contract ? contract.monthlyAmount : 0);
        custs.push({
          slug,
          companyName: c.companyName || slug,
          contractActive: active,
          payActive,
          payout: payActive ? payout : 0,
          remaining,
          endAt: payoutEndDate(m, contract),
        });
      });
      custs.sort((a, b) => b.payout - a.payout);
      const monthlyTotal = custs.reduce((s, x) => s + x.payout, 0);
      return { ...m, custs, monthlyTotal };
    }).sort((a, b) => b.monthlyTotal - a.monthlyTotal || (a.name || '').localeCompare(b.name || ''));
  }, [team, customers, now]);

  // ── Netto-Übersicht ──
  const overview = useMemo(() => {
    let mrr = 0, mrrGross = 0;
    Object.keys(customers).forEach((slug) => {
      const c = customers[slug] || {};
      if (c.contract && c.contract.active && contractRunning(c.contract, now)) {
        const net = Number(c.contract.monthlyAmount) || 0;
        mrr += net;
        mrrGross += grossFromNet(net, c.contract);
      }
    });
    const payoutTotal = members.reduce((s, m) => s + m.monthlyTotal, 0);
    let paidMonth = 0, openSum = 0, openCount = 0;
    list.forEach((p) => {
      if (p.status === 'cancelled') return;
      if (p.status === 'paid') { if (sameMonth(p.paidAt, now)) paidMonth += p.amount || 0; }
      else if (p.status !== 'invoiced') { openSum += p.amount || 0; openCount += 1; }
    });
    return { mrr, mrrGross, payoutTotal, net: mrr - payoutTotal, paidMonth, openSum, openCount };
  }, [customers, members, list, now]);

  // ── Fällige Auszahlungen (persistiert: auto-generiert + manuell) ──
  const payoutList = useMemo(() =>
    Object.keys(payouts).map((id) => ({ id, ...payouts[id] })).sort((a, b) => (b.date || 0) - (a.date || 0)),
  [payouts]);

  const payoutCounts = useMemo(() => ({
    open: payoutList.filter((p) => p.status !== 'paid' && p.status !== 'cancelled').length,
    paid: payoutList.filter((p) => p.status === 'paid').length,
    // Ausgezahlt, aber ohne Rechnung des Freelancers -> Belegnachweis fehlt.
    noinvoice: payoutList.filter(invoiceMissing).length,
    cancelled: payoutList.filter((p) => p.status === 'cancelled').length,
    all: payoutList.length,
  }), [payoutList]);

  const filteredPayouts = useMemo(() => {
    if (outFilter === 'all') return payoutList;
    if (outFilter === 'paid') return payoutList.filter((p) => p.status === 'paid');
    if (outFilter === 'noinvoice') return payoutList.filter(invoiceMissing);
    if (outFilter === 'cancelled') return payoutList.filter((p) => p.status === 'cancelled');
    return payoutList.filter((p) => p.status !== 'paid' && p.status !== 'cancelled');
  }, [payoutList, outFilter]);

  const patchPayment = (id, patch) =>
    update(ref(database, `${PAYMENTS_PATH}/${id}`), { ...patch, updatedAt: serverTimestamp() })
      .catch((e) => console.error('Zahlung aktualisieren fehlgeschlagen:', e));
  // Beim Provisionsmodell steckt der komplette Umsatz in den Terminen. Geht die
  // Rechnung raus, bevor sie erfasst sind, wäre sie über 0 € – und die Beträge
  // sind danach eingefroren. Deshalb einmal nachfragen.
  const apptsOkBeforeInvoicing = (p) => {
    const contract = (customers[p.customerSlug] || {}).contract || null;
    const recorded = typeof p.appointments === 'number' ? p.appointments : null;
    if (!commissionActive(contract) || recorded) return true;
    const rep = reportings[reportingId(p.customerSlug, p.periodKey)];
    const hint = rep && rep.status === 'submitted'
      ? 'Das Reporting meldet 0 Termine.'
      : 'Für diesen Monat wurde noch kein Reporting eingereicht.';
    return window.confirm(`${hint}\n\nDie Rechnung für „${p.companyName}" wird über ${fmtEur(p.amount)} gestellt und ist danach nicht mehr änderbar.\n\nTrotzdem stellen?`);
  };
  const revertToDue = (p) => patchPayment(p.id, { status: 'due', invoicedAt: null, paidAt: null });
  const revertToInvoiced = (p) => patchPayment(p.id, { status: 'invoiced', paidAt: null });
  // Einzelne Rechnung stornieren (mit Pflichtgrund) bzw. Storno aufheben.
  const cancelPayment = (id, reason) => patchPayment(id, { status: 'cancelled', cancelReason: reason, cancelledAt: Date.now(), invoicedAt: null, paidAt: null });
  const uncancelPayment = (p) => patchPayment(p.id, { status: 'due', cancelReason: null, cancelledAt: null });
  // Zukünftige Rechnungen pausieren = Vertrag deaktivieren (wie Sales-Pipeline).
  const pauseCustomerBilling = (slug, reason) =>
    update(ref(database, `${CUSTOMERS_PATH}/${slug}/contract`), { active: false, pausedAt: Date.now(), pauseReason: reason })
      .catch((e) => console.error('Vertrag pausieren fehlgeschlagen:', e));
  const resumeCustomerBilling = (slug) =>
    update(ref(database, `${CUSTOMERS_PATH}/${slug}/contract`), { active: true, pausedAt: null, pauseReason: null })
      .catch((e) => console.error('Vertrag reaktivieren fehlgeschlagen:', e));
  // Termin-Preis (netto) je Kunde. Der intern im Vertrag festgelegte Preis
  // gewinnt; sonst greift der im Sales Research berechnete Wert.
  // 0 = kein Preis hinterlegt.
  const apptPrice = (slug) => {
    const contract = slug && customers[slug] && customers[slug].contract;
    const own = appointmentPriceFor(contract, 0);
    if (own > 0) return Math.round(own);
    const cfg = slug && reports[slug] && reports[slug].config;
    if (!cfg) return 0;
    try {
      const v = computeProjection(cfg).appointmentValue;
      return typeof v === 'number' && v > 0 ? Math.round(v) : 0;
    } catch (e) { return 0; }
  };
  // Zusätzliche, verrechnete Termine setzen: erhöht den Fixbetrag um
  // (Anzahl × Termin-Preis) und hält gleichzeitig das verknüpfte Reporting
  // (qualifizierte Termine) und die Team-Auszahlungen (Bonus je Extra-Termin)
  // konsistent – alles über denselben Periodenschlüssel.
  const setExtraAppointments = (p, count) => {
    const next = Math.max(0, Math.round(Number(count) || 0));
    const curPrice = Number(p.appointmentPrice) || 0;
    const price = curPrice > 0 ? curPrice : apptPrice(p.customerSlug);
    if (next > 0 && price <= 0) return; // ohne hinterlegten Termin-Preis nicht möglich

    // Wiederkehrende Rechnung (slug__periodKey): Reporting + Auszahlungen mitkoppeln.
    const keyed = p.periodKey && p.id === paymentId(p.customerSlug, p.periodKey);
    if (keyed) {
      const updates = buildExtraApptUpdates({
        slug: p.customerSlug,
        periodKey: p.periodKey,
        extra: next,
        customers, team, payments, payouts, reportings,
        apptPrice,
      });
      if (Object.keys(updates).length) {
        update(ref(database), updates).catch((e) => console.error('Extra-Termine aktualisieren fehlgeschlagen:', e));
      }
      return;
    }

    // Einzel-/manuelle Zahlung: nur die Rechnung selbst neu berechnen.
    const baseNet = typeof p.baseNet === 'number'
      ? p.baseNet
      : Math.max(0, (Number(p.net) || 0) - (Number(p.extraAppointments) || 0) * curPrice);
    const net = baseNet + next * price;
    const vat = p.taxMode === 'vat' ? net * (Number(p.vatRate) || 0) / 100 : 0;
    const amount = net + vat;
    patchPayment(p.id, { baseNet, appointmentPrice: price, extraAppointments: next, net, vat, amount });
  };
  const deletePayment = (p) => {
    if (!window.confirm(`Zahlung „${p.companyName}“ über ${fmtEur(p.amount)} wirklich löschen?`)) return;
    remove(ref(database, `${PAYMENTS_PATH}/${p.id}`)).catch((e) => console.error('Zahlung löschen fehlgeschlagen:', e));
    if (p.invoice && p.invoice.path) deleteObject(storageRef(storage, p.invoice.path)).catch(() => {});
  };

  const revertPayout = (po) =>
    update(ref(database, `${PAYOUTS_PATH}/${po.id}`), { status: 'open', paidAt: null, updatedAt: serverTimestamp() })
      .catch((e) => console.error('Auszahlung aktualisieren fehlgeschlagen:', e));
  const cancelPayout = (id, reason) =>
    update(ref(database, `${PAYOUTS_PATH}/${id}`), { status: 'cancelled', cancelReason: reason, cancelledAt: Date.now(), paidAt: null, updatedAt: serverTimestamp() })
      .catch((e) => console.error('Auszahlung stornieren fehlgeschlagen:', e));
  const uncancelPayout = (po) =>
    update(ref(database, `${PAYOUTS_PATH}/${po.id}`), { status: 'open', cancelReason: null, cancelledAt: null, updatedAt: serverTimestamp() })
      .catch((e) => console.error('Auszahlung aktualisieren fehlgeschlagen:', e));
  const deletePayout = (po) => {
    if (!window.confirm(`Auszahlung an „${po.memberName}“ über ${fmtEur(po.amount)} wirklich löschen?`)) return;
    remove(ref(database, `${PAYOUTS_PATH}/${po.id}`)).catch((e) => console.error('Auszahlung löschen fehlgeschlagen:', e));
    if (po.invoice && po.invoice.path) deleteObject(storageRef(storage, po.invoice.path)).catch(() => {});
  };

  // ── Zahlung bestätigen / Rechnung dokumentieren ──
  // Zwischenschritt vor dem Abhaken – für beide Richtungen derselbe Dialog:
  //  • Eingänge: die Rechnung, die wir dem Kunden geschickt haben
  //    (mode 'invoiced' beim Stellen, 'paid' beim Zahlungseingang),
  //  • Auszahlungen: die Rechnung, die der Freelancer uns stellt (mode 'confirm').
  // mode 'attach' dokumentiert nach, ohne am Status zu rühren.
  const invPathFor = (kind) => (kind === 'payment' ? PAYMENTS_PATH : PAYOUTS_PATH);
  const invStorageDirFor = (kind) => (kind === 'payment' ? CUSTOMER_INVOICE_STORAGE : PAYOUT_INVOICE_STORAGE);
  // Bereits gespeicherte Datei dieses Datensatzes – sie bleibt liegen, bis der
  // Dialog bestätigt wird (Abbrechen darf nichts zerstören).
  const invKeptPath = () => (invModal && invModal.item.invoice && invModal.item.invoice.path) || null;

  const openInvModal = (kind, item, mode) => {
    setMenuId(null);
    const doc = invoiceDoc(item);
    setInvModal({ kind, item, mode });
    setInvFile(doc && doc.path ? doc : null);
    setInvLink(doc && !doc.path ? doc.url : '');
    setInvNo((doc && doc.number) || '');
    setInvError('');
    setInvPct(0);
    setInvBusy(false);
  };
  const resetInvModal = () => {
    setInvModal(null); setInvFile(null); setInvLink(''); setInvNo(''); setInvError(''); setInvPct(0);
  };
  const closeInvModal = () => {
    if (invBusy) return;
    // Frisch hochgeladene, aber nicht bestätigte Datei nicht als Waise zurücklassen.
    const kept = invKeptPath();
    if (invFile && invFile.path && invFile.path !== kept) {
      deleteObject(storageRef(storage, invFile.path)).catch(() => {});
    }
    resetInvModal();
  };
  const uploadInvFile = async (file) => {
    if (!file || !invModal) return;
    if (file.size > MAX_INVOICE_BYTES) { setInvError('Datei ist zu groß (max. 25 MB).'); return; }
    setInvError(''); setInvBusy(true); setInvPct(0);
    const safeName = (file.name || 'rechnung').replace(/[^\w.-]+/g, '_').slice(0, 120);
    const path = `${invStorageDirFor(invModal.kind)}/${invModal.item.id}/${Date.now()}_${safeName}`;
    const sRef = storageRef(storage, path);
    try {
      await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(sRef, file, { contentType: file.type || 'application/octet-stream' });
        task.on(
          'state_changed',
          (snap) => setInvPct(snap.totalBytes ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100) : 0),
          reject,
          resolve,
        );
      });
      const url = await getDownloadURL(sRef);
      // Ersetzt eine zuvor in diesem Dialog hochgeladene Datei -> die alte weg.
      const kept = invKeptPath();
      if (invFile && invFile.path && invFile.path !== kept) {
        deleteObject(storageRef(storage, invFile.path)).catch(() => {});
      }
      setInvFile({
        url,
        name: (file.name || safeName).slice(0, 200),
        size: file.size,
        type: (file.type || '').slice(0, 120),
        path,
        uploadedAt: Date.now(),
      });
    } catch (err) {
      setInvError('Upload fehlgeschlagen: ' + ((err && err.message) || 'Unbekannter Fehler'));
    } finally {
      setInvBusy(false);
      setInvPct(0);
    }
  };
  const removeInvFile = () => {
    const kept = invKeptPath();
    if (invFile && invFile.path && invFile.path !== kept) {
      deleteObject(storageRef(storage, invFile.path)).catch(() => {});
    }
    setInvFile(null);
  };
  const confirmInvModal = () => {
    if (!invModal || invBusy) return;
    const { kind, item, mode } = invModal;
    const link = invLink.trim();
    const number = invNo.trim().slice(0, 60);
    const patch = { updatedAt: serverTimestamp() };
    if (mode === 'invoiced') { patch.status = 'invoiced'; patch.invoicedAt = Date.now(); }
    if (mode === 'paid' || mode === 'confirm') { patch.status = 'paid'; patch.paidAt = Date.now(); }
    if (invFile || link) {
      const rec = invFile
        ? {
          url: invFile.url,
          name: invFile.name || 'Rechnung',
          path: invFile.path,
          uploadedAt: invFile.uploadedAt || Date.now(),
        }
        : { url: link.slice(0, 2000), uploadedAt: Date.now() };
      if (invFile && typeof invFile.size === 'number') rec.size = invFile.size;
      if (invFile && invFile.type) rec.type = invFile.type;
      if (number) rec.number = number;
      patch.invoice = rec;
      // Der frühere Nur-Link der Eingänge ist damit abgelöst.
      if (item.invoiceUrl) patch.invoiceUrl = null;
    } else {
      if (item.invoice) patch.invoice = null; // im Dialog entfernt
      if (item.invoiceUrl) patch.invoiceUrl = null;
    }
    const oldPath = (item.invoice && item.invoice.path) || null;
    const keepsOldFile = !!(invFile && invFile.path === oldPath);
    update(ref(database, `${invPathFor(kind)}/${item.id}`), patch)
      .then(() => { if (oldPath && !keepsOldFile) deleteObject(storageRef(storage, oldPath)).catch(() => {}); })
      .catch((e) => console.error('Zahlung aktualisieren fehlgeschlagen:', e));
    resetInvModal();
  };

  // ── Modal öffnen / speichern ──
  const openPaymentModal = () => {
    setForm({ customerSlug: '', companyName: '', amount: '', dueDate: todayInput(), note: '' });
    setModal('payment');
  };
  const openPayoutModal = () => {
    setForm({ memberId: '', customerSlug: '', amount: '', date: todayInput(), note: '' });
    setModal('payout');
  };
  const closeModal = () => { if (!saving) { setModal(null); setForm({}); } };

  // ── Storno-Modal (Pflichtgrund) ──
  const openCancel = (kind, item, scope) => { setMenuId(null); setCancelReason(''); setCancelModal({ kind, item, scope }); };
  const closeCancel = () => { setCancelModal(null); setCancelReason(''); };
  const confirmCancel = () => {
    const reason = (cancelReason || '').trim();
    if (!reason || !cancelModal) return;
    const { kind, item, scope } = cancelModal;
    if (scope === 'future') pauseCustomerBilling(item.customerSlug, reason);
    else if (kind === 'payment') cancelPayment(item.id, reason);
    else cancelPayout(item.id, reason);
    closeCancel();
  };

  const onPickCustomer = (slug) => {
    const c = customers[slug] || {};
    setForm((f) => ({ ...f, customerSlug: slug, companyName: slug ? (c.companyName || slug) : f.companyName }));
  };

  const saveManualPayment = async () => {
    const amount = Math.round(Number(form.amount));
    const companyName = (form.companyName || '').trim() || (form.customerSlug ? (customers[form.customerSlug]?.companyName || form.customerSlug) : '');
    if (!companyName) { window.alert('Bitte einen Kunden wählen oder eine Bezeichnung eingeben.'); return; }
    if (!isFinite(amount) || amount <= 0) { window.alert('Bitte einen gültigen Betrag eingeben.'); return; }
    const id = `manual__${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
    const rec = {
      customerSlug: form.customerSlug || 'manual',
      companyName,
      type: 'manual',
      dueDate: dateInputToMs(form.dueDate),
      amount,
      status: 'due',
      source: 'manual',
      createdAt: serverTimestamp(),
    };
    const pickedCustId = form.customerSlug ? customers[form.customerSlug]?.customerId : null;
    if (typeof pickedCustId === 'number') rec.customerId = pickedCustId;
    if ((form.note || '').trim()) rec.note = form.note.trim();
    setSaving(true);
    try {
      rec.invoiceNo = await reserveInvoiceNumbers(1);
      await set(ref(database, `${PAYMENTS_PATH}/${id}`), rec);
      setModal(null); setForm({});
    } catch (e) {
      console.error('Zahlung anlegen fehlgeschlagen:', e); window.alert('Speichern fehlgeschlagen: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveManualPayout = () => {
    const amount = Math.round(Number(form.amount));
    if (!form.memberId) { window.alert('Bitte ein Teammitglied wählen.'); return; }
    if (!isFinite(amount) || amount <= 0) { window.alert('Bitte einen gültigen Betrag eingeben.'); return; }
    const m = team[form.memberId] || {};
    const c = form.customerSlug ? (customers[form.customerSlug] || {}) : null;
    const id = `manual__${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
    const rec = {
      memberId: form.memberId,
      memberName: m.name || form.memberId,
      amount,
      date: dateInputToMs(form.date),
      status: 'open',
      source: 'manual',
      createdAt: serverTimestamp(),
    };
    if (form.customerSlug) { rec.customerSlug = form.customerSlug; rec.companyName = c?.companyName || form.customerSlug; }
    if ((form.note || '').trim()) rec.note = form.note.trim();
    setSaving(true);
    set(ref(database, `${PAYOUTS_PATH}/${id}`), rec)
      .then(() => { setModal(null); setForm({}); })
      .catch((e) => { console.error('Auszahlung anlegen fehlgeschlagen:', e); window.alert('Speichern fehlgeschlagen: ' + e.message); })
      .finally(() => setSaving(false));
  };

  const customerOptions = useMemo(() =>
    Object.keys(customers)
      .map((slug) => ({ slug, name: customers[slug]?.companyName || slug }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  [customers]);
  const teamOptions = useMemo(() =>
    Object.keys(team)
      .map((id) => ({ id, name: team[id]?.name || id }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  [team]);

  const loading = loadingC || loadingT || loadingP || loadingPo;
  const isOverdue = (p) => p.status !== 'paid' && p.status !== 'cancelled' && p.dueDate && p.dueDate < now - 7 * 86400000;

  return (
    <div className="pay-wrap">
      <motion.div className="pay-inner" variants={staggerContainer} initial="initial" animate="animate">
        <motion.header className="pay-head" variants={itemVariants}>
          <div className="pay-head-text">
            <h1>Zahlungen &amp; Provisionen</h1>
            <p>Alle Geldflüsse in Echtzeit – Einnahmen aus Kundenverträgen, Auszahlungen ans Team und das Netto, das am Ende bleibt.</p>
          </div>
          <div className="pay-head-actions">
            <div className="fin-seg fin-seg-3" role="tablist" aria-label="Ansicht wählen">
              <span className={`fin-seg-thumb ${view === 'out' ? 'fin-seg-thumb-out' : ''} ${view === 'reporting' ? 'fin-seg-thumb-rep' : ''}`} aria-hidden="true" />
              <button type="button" role="tab" aria-selected={view === 'in'} className={`fin-seg-btn ${view === 'in' ? 'fin-seg-btn-on' : ''}`} onClick={() => setView('in')}>Eingänge</button>
              <button type="button" role="tab" aria-selected={view === 'out'} className={`fin-seg-btn ${view === 'out' ? 'fin-seg-btn-on' : ''}`} onClick={() => setView('out')}>Auszahlungen</button>
              <button type="button" role="tab" aria-selected={view === 'reporting'} className={`fin-seg-btn ${view === 'reporting' ? 'fin-seg-btn-on' : ''}`} onClick={() => setView('reporting')}>Reportings</button>
            </div>
            {view !== 'reporting' && (
              <button
                type="button"
                className="pay-add-btn"
                onClick={() => (view === 'in' ? openPaymentModal() : openPayoutModal())}
                title={view === 'in' ? 'Einzelne Zahlung hinzufügen' : 'Einzelne Provision hinzufügen'}
              >
                <span className="pay-add-plus" aria-hidden="true">+</span>
                {view === 'in' ? 'Zahlung' : 'Provision'}
              </button>
            )}
          </div>
        </motion.header>

        {/* Netto-Übersicht: rein vs. raus */}
        {view !== 'reporting' && (
        <motion.div className="pay-kpis" variants={staggerContainer}>
          <motion.div className="pay-kpi pay-kpi-green" variants={subtleItemVariants}>
            <div className="pay-kpi-val">+{fmtEur(overview.mrr)}</div>
            <div className="pay-kpi-lbl">Einnahmen / Monat (netto)</div>
            <div className="pay-kpi-sub">{overview.mrrGross > overview.mrr ? `${fmtEur(overview.mrrGross)} brutto inkl. IVA` : 'Wiederkehrend aus aktiven Verträgen'}</div>
          </motion.div>
          <motion.div className="pay-kpi pay-kpi-amber" variants={subtleItemVariants}>
            <div className="pay-kpi-val">−{fmtEur(overview.payoutTotal)}</div>
            <div className="pay-kpi-lbl">Kosten / Monat</div>
            <div className="pay-kpi-sub">Provisionen &amp; Fixvergütungen ans Team</div>
          </motion.div>
          <motion.div className="pay-kpi pay-kpi-accent" variants={subtleItemVariants}>
            <div className="pay-kpi-val">{fmtEur(overview.net)}</div>
            <div className="pay-kpi-lbl">Bleibt / Monat (netto)</div>
            <div className="pay-kpi-sub">Einnahmen minus Team-Kosten</div>
          </motion.div>
          <motion.div className="pay-kpi" variants={subtleItemVariants}>
            <div className="pay-kpi-val">{fmtEur(overview.paidMonth)}</div>
            <div className="pay-kpi-lbl">Diesen Monat bezahlt</div>
            <div className="pay-kpi-sub">{overview.openCount} offen · {fmtEur(overview.openSum)}</div>
          </motion.div>
        </motion.div>
        )}

        {view === 'reporting' ? (
          <ReportingDashboard embedded readOnly />
        ) : loading ? (
          <div className="pay-empty">Daten werden geladen …</div>
        ) : view === 'in' ? (
          <>
            {apptTodo.length > 0 && (
              <motion.div className="pay-todo" variants={itemVariants}>
                <span className="pay-todo-icon" aria-hidden="true">!</span>
                <div className="pay-todo-text">
                  <strong>
                    {apptTodo.length === 1
                      ? 'Bei 1 Rechnung fehlen noch die Termine.'
                      : `Bei ${apptTodo.length} Rechnungen fehlen noch die Termine.`}
                  </strong>
                  <span>
                    Diese Kunden werden pro Termin abgerechnet. Trage die generierten Termine unten in der Zeile ein –
                    solange sie fehlen, geht die Rechnung über den reinen Retainer raus und das Team bekommt keine Provision.
                  </span>
                  <span className="pay-todo-list">
                    {apptTodo.slice(0, 6).map((p) => (
                      <span key={p.id} className="pay-todo-chip">
                        {p.companyName} · {monthLabel(p.periodKey)}
                      </span>
                    ))}
                    {apptTodo.length > 6 && <span className="pay-todo-chip">+{apptTodo.length - 6} weitere</span>}
                  </span>
                </div>
              </motion.div>
            )}

            <motion.div className="pay-filters" variants={itemVariants}>
              {FILTERS.map((f) => (
                <button key={f.id} type="button" className={`pay-filter ${filter === f.id ? 'pay-filter-active' : ''}`} onClick={() => setFilter(f.id)}>
                  {f.label}<span className="pay-filter-count">{counts[f.id]}</span>
                </button>
              ))}
            </motion.div>

            {filtered.length === 0 ? (
              <div className="pay-empty">
                {filter === 'open'
                  ? 'Aktuell keine offenen Rechnungen. Sobald du beim Kick-off-Call die Zahlung startest (Kunde → Deal & Team → Zahlung starten), erscheinen die fälligen Rechnungen automatisch hier – brutto inkl. der automatisch erkannten IVA/Reverse-Charge.'
                  : filter === 'noinvoice'
                    ? 'Sauber – zu jeder gestellten Rechnung liegt das Dokument im System.'
                    : 'Hier gibt es noch nichts zu sehen.'}
              </div>
            ) : (
              <motion.div className="pay-list" variants={staggerContainer}>
                {filtered.map((p) => {
                  const overdue = isOverdue(p);
                  const manual = p.source === 'manual';
                  const cancelled = p.status === 'cancelled';
                  const srcLbl = sourceLabel(p.source);
                  const oneTime = p.type === 'onetime';
                  // Einmalzahlungen zeigen statt eines Monats ihre Bezeichnung
                  // („Beratung Q1“) – die ist für die Zuordnung das Entscheidende.
                  const periodLbl = manual
                    ? (p.note || 'Einzelzahlung')
                    : oneTime ? (p.label || 'Einmalzahlung') : monthLabel(p.periodKey);
                  const badge = cancelled ? { c: 'cancelled', t: 'storniert' }
                    : p.status === 'invoiced' ? { c: 'invoiced', t: 'gestellt' }
                    : p.status === 'paid' ? { c: 'paid', t: 'bezahlt' }
                    : overdue ? { c: 'overdue', t: 'überfällig' }
                    : { c: 'due', t: 'offen' };
                  const recurring = p.type === 'recurring' && p.customerSlug && customers[p.customerSlug] && customers[p.customerSlug].contract;
                  const billingActive = recurring && customers[p.customerSlug].contract.active;
                  const extraAppts = Number(p.extraAppointments) || 0;
                  const apptUnit = p.appointmentPrice > 0 ? p.appointmentPrice : apptPrice(p.customerSlug);
                  // Termine sind editierbar, solange die Rechnung noch offen ist (vor Rechnungsstellung).
                  const canEditAppts = !cancelled && p.status === 'due' && p.type !== 'setup' && !oneTime && apptUnit > 0;
                  // Beim Provisionsmodell ohne Freikontingent ist der Zähler die
                  // GESAMTZAHL der Termine des Monats, nicht nur die Extras.
                  const payContract = p.customerSlug && customers[p.customerSlug] && customers[p.customerSlug].contract;
                  const apptsAreTotal = commissionActive(payContract) && includedAppointmentsFor(payContract) === 0;
                  const apptWord = apptsAreTotal ? 'Termine' : 'Extra-Termine';
                  const comm = commissionPreview(p);
                  const apptsMissing = comm.needsAppts && p.status !== 'paid';
                  const doc = invoiceDoc(p);
                  return (
                    <motion.div key={p.id} variants={subtleItemVariants} className={`pay-row ${p.status === 'paid' ? 'pay-row-paid' : ''} ${overdue ? 'pay-row-overdue' : ''} ${cancelled ? 'pay-row-cancelled' : ''} ${apptsMissing ? 'pay-row-todo' : ''}`}>
                      <div className="pay-row-main">
                        <span className="pay-row-name">
                          {typeof p.invoiceNo === 'number' && <span className="pay-invno">{fmtInvoiceNo(p.invoiceNo)}</span>}
                          {p.companyName}
                          {p.type === 'setup' && <span className="pay-type-setup">Setup</span>}
                          {oneTime && <span className="pay-type-once">Einmalig</span>}
                          {srcLbl && <span className={`pay-src pay-src-${manual ? 'manual' : 'auto'}`}>{srcLbl}</span>}
                        </span>
                        <span className="pay-row-meta">
                          {typeof p.customerId === 'number' ? `${fmtCustomerId(p.customerId)} · ` : ''}{periodLbl} · fällig {fmtDate(p.dueDate)}{p.paidAt ? ` · bezahlt ${fmtDate(p.paidAt)}` : ''}
                          {cancelled && p.cancelReason ? ` · Storno: ${p.cancelReason}` : ''}
                        </span>
                      </div>
                      <div className="pay-row-amount">
                        <div className="pay-row-amount-val">{fmtEur(p.amount)}</div>
                        <div className="pay-row-amount-lbl">
                          {p.taxMode === 'vat'
                            ? `brutto · netto ${fmtEur(p.net)} + ${p.vatRate}% IVA`
                            : 'Betrag · ohne IVA (Reverse Charge)'}
                        </div>
                        {extraAppts > 0 && (
                          <div className="pay-row-amount-appts">{apptsAreTotal ? '' : '+'}{extraAppts} Termin{extraAppts === 1 ? '' : 'e'} × {fmtEur(apptUnit)}</div>
                        )}
                      </div>
                      {(canEditAppts || extraAppts > 0) && (
                        <div className="pay-appts" title={`Abgerechnete ${apptWord} dieser Periode · je ${fmtEur(apptUnit)} netto`}>
                          {canEditAppts ? (
                            <>
                              <button type="button" className="pay-appts-btn" disabled={extraAppts <= 0} aria-label="Termin entfernen" onClick={() => setExtraAppointments(p, extraAppts - 1)}>−</button>
                              <span className="pay-appts-mid">
                                <ApptCountInput
                                  value={extraAppts}
                                  label={apptWord}
                                  onCommit={(n) => setExtraAppointments(p, n)}
                                />
                                <span className="pay-appts-cap">{apptWord} · je {fmtEur(apptUnit)}</span>
                              </span>
                              <button type="button" className="pay-appts-btn pay-appts-add" aria-label="Termin hinzufügen" onClick={() => setExtraAppointments(p, extraAppts + 1)}>+</button>
                            </>
                          ) : (
                            <span className="pay-appts-static">{apptsAreTotal ? '' : '+'}{extraAppts} Termin{extraAppts === 1 ? '' : 'e'}</span>
                          )}
                        </div>
                      )}
                      {comm.active && (
                        <div
                          className={`pay-comm ${apptsMissing ? 'pay-comm-todo' : ''}`}
                          title={comm.shares.length
                            ? comm.shares.map((s) => `${s.name} (${s.roleLabel}): ${fmtEur(s.amount)}`).join('\n')
                            : 'Diesem Kunden ist noch niemand mit Provisionsstaffel zugewiesen.'}
                        >
                          {apptsMissing ? (
                            <>
                              <span className="pay-comm-val">Termine fehlen</span>
                              <span className="pay-comm-lbl">Provision offen</span>
                            </>
                          ) : (
                            <>
                              <span className="pay-comm-val">{fmtEur(comm.total)}</span>
                              <span className="pay-comm-lbl">
                                {p.status === 'paid'
                                  ? `Provision · ${comm.appointments} Term.`
                                  : `vorgemerkt · ${comm.appointments} Term.`}
                              </span>
                            </>
                          )}
                        </div>
                      )}
                      {/* Liegt die Rechnung, die der Kunde bekommen hat, bei uns
                          im System? Grün = abgelegt und direkt öffenbar, amber =
                          Rechnung ist raus, das Dokument fehlt noch. */}
                      {doc ? (
                        <a
                          className="pay-inv pay-inv-ok"
                          href={doc.url}
                          target="_blank"
                          rel="noreferrer"
                          title={`Rechnung öffnen${doc.number ? ` · Nr. ${doc.number}` : ''}${doc.name ? ` · ${doc.name}` : ''}`}
                        >
                          <span aria-hidden="true">📄</span>Beleg
                        </a>
                      ) : invoiceMissing(p) ? (
                        <button
                          type="button"
                          className="pay-inv pay-inv-missing"
                          onClick={() => openInvModal('payment', p, 'attach')}
                          title="Gestellte Rechnung nachträglich ablegen"
                        >
                          <span aria-hidden="true">!</span>Beleg fehlt
                        </button>
                      ) : null}
                      <span className={`pay-badge pay-badge-${badge.c}`}>{badge.t}</span>
                      <div className="pay-row-actions">
                        {p.status === 'due' && <button type="button" className="pay-btn pay-btn-primary" onClick={() => { if (apptsOkBeforeInvoicing(p)) openInvModal('payment', p, 'invoiced'); }}>Rechnung gestellt</button>}
                        {p.status === 'invoiced' && <button type="button" className="pay-btn pay-btn-green" onClick={() => openInvModal('payment', p, 'paid')}>Bezahlt ✓</button>}
                        {p.status === 'invoiced' && <button type="button" className="pay-btn pay-btn-ghost" onClick={() => revertToDue(p)}>↩</button>}
                        {p.status === 'paid' && <button type="button" className="pay-btn pay-btn-ghost" onClick={() => revertToInvoiced(p)}>↩ offen</button>}
                        {manual && <button type="button" className="pay-btn pay-btn-ghost pay-btn-del" onClick={() => deletePayment(p)} title="Manuelle Zahlung löschen">✕</button>}
                        <div className="pay-menu-wrap" onClick={(e) => e.stopPropagation()}>
                          <button type="button" className="pay-menu-btn" aria-label="Weitere Aktionen" aria-haspopup="true" onClick={() => setMenuId(menuId === p.id ? null : p.id)}>⋯</button>
                          {menuId === p.id && (
                            <div className="pay-menu" role="menu">
                              {!cancelled && (
                                <button type="button" className="pay-menu-item" onClick={() => openInvModal('payment', p, 'attach')}>
                                  {doc ? 'Rechnung ersetzen …' : 'Rechnung anhängen …'}
                                </button>
                              )}
                              {cancelled ? (
                                <button type="button" className="pay-menu-item" onClick={() => { setMenuId(null); uncancelPayment(p); }}>Storno aufheben</button>
                              ) : (
                                <button type="button" className="pay-menu-item pay-menu-item-danger" onClick={() => openCancel('payment', p, 'single')}>Rechnung stornieren …</button>
                              )}
                              {recurring && (
                                billingActive
                                  ? <button type="button" className="pay-menu-item pay-menu-item-danger" onClick={() => openCancel('payment', p, 'future')}>Zukünftige Rechnungen pausieren …</button>
                                  : <button type="button" className="pay-menu-item" onClick={() => { setMenuId(null); resumeCustomerBilling(p.customerSlug); }}>Zukünftige wieder aktivieren</button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}

            {upcoming.length > 0 && (
              <>
                <motion.div className="pay-section-head" variants={itemVariants}>Demnächst fällig</motion.div>
                <motion.div className="pay-list" variants={staggerContainer}>
                  {upcoming.map((u) => (
                    <motion.div key={u.key} variants={subtleItemVariants} className="pay-row">
                      <div className="pay-row-main">
                        <span className="pay-row-name">
                          {u.companyName}
                          {u.type === 'onetime' && <span className="pay-type-once">Einmalig</span>}
                        </span>
                        <span className="pay-row-meta">{u.title} · fällig {fmtDate(u.dueDate)}</span>
                      </div>
                      <div className="pay-row-amount">
                        <div className="pay-row-amount-val">{fmtEur(u.amount)}</div>
                        <div className="pay-row-amount-lbl">geplant</div>
                      </div>
                      <span className="pay-badge pay-badge-due">geplant</span>
                    </motion.div>
                  ))}
                </motion.div>
              </>
            )}
          </>
        ) : (
          // ── Auszahlungen ──
          <>
          {/* Zuerst die Antwort auf die wichtigste Frage: welcher Mitarbeiter
              bekommt insgesamt noch wie viel – fällig plus voraussichtlich.
              Jede:r sieht dieselbe Aufstellung über den eigenen Auszahlungs-Link
              (Settings → Team & Provisionen → Link anzeigen). */}
          {memberSummary.length > 0 && (
            <>
              <motion.div className="pay-section-head" variants={itemVariants}>Wer bekommt noch was?</motion.div>
              <motion.div className="pay-list" variants={staggerContainer}>
                {memberSummary.map((m) => (
                  <motion.div key={m.memberId} variants={subtleItemVariants} className="pay-row pay-row-pending">
                    <div className="pay-row-main">
                      <span className="pay-row-name">
                        {m.name}
                        {m.role && <span className="pay-type-pending">{m.role}</span>}
                      </span>
                      <span className="pay-row-meta">
                        {fmtEur(m.open)} fällig · {fmtEur(m.expected)} voraussichtlich · {fmtEur(m.paid)} bereits ausgezahlt
                        {m.appointmentsPending > 0
                          ? ` · bei ${m.appointmentsPending} Abrechnung${m.appointmentsPending === 1 ? '' : 'en'} fehlen die Termine`
                          : ''}
                      </span>
                    </div>
                    <div className="pay-row-amount">
                      <div className="pay-row-amount-val">−{fmtEur(m.outstanding)}</div>
                      <div className="pay-row-amount-lbl">bekommt noch</div>
                    </div>
                    <span className="pay-badge pay-badge-pending">{m.openCount + m.expectedCount} Posten</span>
                  </motion.div>
                ))}
              </motion.div>
            </>
          )}

          {/* Fällige Auszahlungen: erst nach Zahlung des Kunden freigeschaltet, je
              abhakbar. Spiegelbild der Eingänge – hier werden eure Kosten bezahlt. */}
          <motion.div className="pay-section-head" variants={itemVariants}>Fällige Auszahlungen</motion.div>
          <motion.div className="pay-filters" variants={itemVariants}>
            {OUT_FILTERS.map((f) => (
              <button key={f.id} type="button" className={`pay-filter ${outFilter === f.id ? 'pay-filter-active' : ''}`} onClick={() => setOutFilter(f.id)}>
                {f.label}<span className="pay-filter-count">{payoutCounts[f.id]}</span>
              </button>
            ))}
          </motion.div>

          {filteredPayouts.length === 0 ? (
            <div className="pay-empty">
              {outFilter === 'open'
                ? 'Aktuell keine offenen Auszahlungen. Auszahlungen werden freigeschaltet, sobald der Kunde die zugehörige Monatsrechnung bezahlt hat (Eingänge → „Bezahlt ✓“). Dann erscheint hier automatisch die fällige Provision/Fixvergütung jedes im Auftrag beteiligten Teammitglieds – zum Abhaken, sobald ausgezahlt.'
                : outFilter === 'noinvoice'
                  ? 'Sauber – zu jeder ausgezahlten Provision ist die Rechnung des Freelancers hinterlegt.'
                  : 'Hier gibt es noch nichts zu sehen.'}
            </div>
          ) : (
            <motion.div className="pay-list" variants={staggerContainer}>
              {filteredPayouts.map((po) => {
                const manual = po.source === 'manual';
                const cancelled = po.status === 'cancelled';
                const overdue = po.status !== 'paid' && !cancelled && po.date && po.date < now - 7 * 86400000;
                const oneTime = po.type === 'onetime';
                const periodLbl = manual
                  ? (po.note || 'Einzelauszahlung')
                  : oneTime ? (po.label || 'Einmalzahlung') : monthLabel(po.periodKey);
                // Bei Einmalzahlungen ist die Herleitung interessanter als die
                // Vergütungsart: „10 % von 1.500 €“ erklärt den Betrag sofort.
                // Bei der Terminstaffel erklärt die Rolle plus die Terminzahl den
                // Betrag – ohne sie ist nicht nachvollziehbar, wie er zustande kam.
                const poRole = po.commissionRole ? commissionRoleInfo(po.commissionRole) : null;
                const kindLbl = oneTime
                  ? (po.commissionMode === 'percent'
                    ? `${po.commissionValue} % von ${fmtEur(po.commissionBase)}`
                    : 'Fixbetrag')
                  : poRole
                    ? `${poRole.label} · ${Number(po.appointments) || 0} Termine`
                    : po.payType === 'commission' ? 'Provision' : po.payType === 'fixed' ? 'Fixvergütung' : 'Auszahlung';
                const badge = cancelled ? { c: 'cancelled', t: 'storniert' }
                  : po.status === 'paid' ? { c: 'paid', t: 'bezahlt' }
                  : overdue ? { c: 'overdue', t: 'überfällig' }
                  : { c: 'due', t: 'offen' };
                const invoice = invoiceDoc(po);
                return (
                  <motion.div key={po.id} variants={subtleItemVariants} className={`pay-row ${po.status === 'paid' ? 'pay-row-paid' : ''} ${overdue ? 'pay-row-overdue' : ''} ${cancelled ? 'pay-row-cancelled' : ''}`}>
                    <div className="pay-row-main">
                      <span className="pay-row-name">
                        {po.memberName}
                        {oneTime
                          ? <span className="pay-type-once">Einmalig</span>
                          : po.payType === 'fixed' && <span className="pay-type-setup">Fix</span>}
                        <span className={`pay-src pay-src-${manual ? 'manual' : 'auto'}`}>{manual ? 'Manuell' : 'Auto'}</span>
                      </span>
                      <span className="pay-row-meta">
                        {typeof po.memberNo === 'number' ? `${fmtMemberNo(po.memberNo)} · ` : ''}{po.companyName ? `${po.companyName} · ` : ''}{periodLbl} · ausgestellt {fmtDate(po.date)}{po.paidAt ? ` · bezahlt ${fmtDate(po.paidAt)}` : ''}
                        {cancelled && po.cancelReason ? ` · Storno: ${po.cancelReason}` : ''}
                      </span>
                    </div>
                    <div className="pay-row-amount">
                      <div className="pay-row-amount-val">−{fmtEur(po.amount)}</div>
                      <div className="pay-row-amount-lbl">{kindLbl}</div>
                    </div>
                    {/* Rechnungsnachweis auf einen Blick: hinterlegte Rechnung
                        öffnet direkt, fehlt sie nach der Auszahlung, führt der
                        Hinweis in denselben Dialog zum Nachreichen. */}
                    {invoice ? (
                      <a
                        className="pay-inv pay-inv-ok"
                        href={invoice.url}
                        target="_blank"
                        rel="noreferrer"
                        title={`Rechnung öffnen${invoice.number ? ` · Nr. ${invoice.number}` : ''}${invoice.name ? ` · ${invoice.name}` : ''}`}
                      >
                        <span aria-hidden="true">📄</span>Rechnung
                      </a>
                    ) : invoiceMissing(po) ? (
                      <button
                        type="button"
                        className="pay-inv pay-inv-missing"
                        onClick={() => openInvModal('payout', po, 'attach')}
                        title="Rechnung des Freelancers nachträglich hochladen"
                      >
                        <span aria-hidden="true">!</span>Rechnung fehlt
                      </button>
                    ) : null}
                    <span className={`pay-badge pay-badge-${badge.c}`}>{badge.t}</span>
                    <div className="pay-row-actions">
                      {!cancelled && (po.status !== 'paid'
                        ? <button type="button" className="pay-btn pay-btn-green" onClick={() => openInvModal('payout', po, 'confirm')}>Bezahlt ✓</button>
                        : <button type="button" className="pay-btn pay-btn-ghost" onClick={() => revertPayout(po)}>↩ offen</button>)}
                      {manual && <button type="button" className="pay-btn pay-btn-ghost pay-btn-del" onClick={() => deletePayout(po)} title="Auszahlung löschen">✕</button>}
                      <div className="pay-menu-wrap" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="pay-menu-btn" aria-label="Weitere Aktionen" aria-haspopup="true" onClick={() => setMenuId(menuId === po.id ? null : po.id)}>⋯</button>
                        {menuId === po.id && (
                          <div className="pay-menu" role="menu">
                            {!cancelled && (
                              <button type="button" className="pay-menu-item" onClick={() => openInvModal('payout', po, 'attach')}>
                                {invoice ? 'Rechnung ersetzen …' : 'Rechnung anhängen …'}
                              </button>
                            )}
                            {cancelled
                              ? <button type="button" className="pay-menu-item" onClick={() => { setMenuId(null); uncancelPayout(po); }}>Storno aufheben</button>
                              : <button type="button" className="pay-menu-item pay-menu-item-danger" onClick={() => openCancel('payout', po, 'single')}>Auszahlung stornieren …</button>}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          {/* Ohne erfasste Termine steht die Provision beim Provisionsmodell
              noch nicht fest – der voraussichtliche Betrag oben ist dann zu niedrig. */}
          {apptTodo.length > 0 && (
            <motion.div className="pay-todo" variants={itemVariants}>
              <span className="pay-todo-icon" aria-hidden="true">!</span>
              <div className="pay-todo-text">
                <strong>
                  {apptTodo.length === 1
                    ? 'Bei 1 Rechnung fehlen noch die Termine.'
                    : `Bei ${apptTodo.length} Rechnungen fehlen noch die Termine.`}
                </strong>
                <span>
                  Die Provision steht erst fest, wenn die Termine erfasst sind. Trage sie unter <strong>Eingänge</strong> in der jeweiligen Rechnung ein.
                </span>
              </div>
            </motion.div>
          )}

          {/* Laufende monatliche Kosten je Mitarbeiter – kompakte Vorschau (keine
              abhakbaren Posten, die entstehen erst mit der Zahlung des Kunden). */}
          <motion.div className="pay-section-head" variants={itemVariants}>Monatliche Kosten je Mitarbeiter (laufend)</motion.div>
          {members.length === 0 ? (
            <div className="pay-empty">Noch keine Teammitglieder angelegt. Erstelle sie unter <strong>Settings</strong> und weise sie in der Sales Pipeline einem Kunden zu, um Auszahlungen zu berechnen.</div>
          ) : (
            <motion.div className="pay-mini-list" variants={staggerContainer}>
              {members.map((m) => (
                <motion.div key={m.id} variants={subtleItemVariants} className="pay-mini">
                  <span className="pay-mini-name">{m.name || '(ohne Name)'}</span>
                  <span className="pay-mini-model">
                    {m.payType === 'fixed' ? `Fix ${fmtEur(Number(m.fixedAmount) || 0)}` : `${Number(m.commissionPercent) || 0}%`}
                    {' · '}
                    {m.custs.length === 0 ? 'kein Kunde' : `${m.custs.length} Kunde${m.custs.length === 1 ? '' : 'n'}`}
                  </span>
                  <span className="pay-mini-val">{fmtEur(m.monthlyTotal)}<span className="pay-mini-unit"> / Mt.</span></span>
                </motion.div>
              ))}
            </motion.div>
          )}
          </>
        )}
      </motion.div>

      {modal && (
        <div className="pay-modal-overlay" onMouseDown={closeModal}>
          <motion.div
            className="pay-modal"
            onMouseDown={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
          >
            <div className="pay-modal-head">
              <h2>{modal === 'payment' ? 'Einzelne Zahlung hinzufügen' : 'Einzelne Provision hinzufügen'}</h2>
              <button type="button" className="pay-modal-close" onClick={closeModal} aria-label="Schließen">✕</button>
            </div>

            {modal === 'payment' ? (
              <div className="pay-modal-body">
                <label className="pay-field">
                  <span className="pay-field-lbl">Kunde</span>
                  <select className="pay-select" value={form.customerSlug || ''} onChange={(e) => onPickCustomer(e.target.value)}>
                    <option value="">— Kein Kunde / frei —</option>
                    {customerOptions.map((o) => <option key={o.slug} value={o.slug}>{o.name}</option>)}
                  </select>
                </label>
                <label className="pay-field">
                  <span className="pay-field-lbl">Bezeichnung</span>
                  <input className="pay-input" type="text" placeholder="z. B. Firmenname oder Beschreibung" value={form.companyName || ''} onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))} />
                </label>
                <div className="pay-field-row">
                  <label className="pay-field">
                    <span className="pay-field-lbl">Betrag (€)</span>
                    <input className="pay-input" type="number" min="0" step="1" placeholder="0" value={form.amount || ''} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
                  </label>
                  <label className="pay-field">
                    <span className="pay-field-lbl">Fällig am</span>
                    <input className="pay-input" type="date" value={form.dueDate || ''} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} />
                  </label>
                </div>
                <label className="pay-field">
                  <span className="pay-field-lbl">Notiz (optional)</span>
                  <input className="pay-input" type="text" placeholder="z. B. Zusatzleistung, Einmalrechnung …" value={form.note || ''} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
                </label>
              </div>
            ) : (
              <div className="pay-modal-body">
                <label className="pay-field">
                  <span className="pay-field-lbl">Teammitglied</span>
                  <select className="pay-select" value={form.memberId || ''} onChange={(e) => setForm((f) => ({ ...f, memberId: e.target.value }))}>
                    <option value="">— Bitte wählen —</option>
                    {teamOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </label>
                <label className="pay-field">
                  <span className="pay-field-lbl">Kunde (optional)</span>
                  <select className="pay-select" value={form.customerSlug || ''} onChange={(e) => setForm((f) => ({ ...f, customerSlug: e.target.value }))}>
                    <option value="">— Kein Kunde —</option>
                    {customerOptions.map((o) => <option key={o.slug} value={o.slug}>{o.name}</option>)}
                  </select>
                </label>
                <div className="pay-field-row">
                  <label className="pay-field">
                    <span className="pay-field-lbl">Betrag (€)</span>
                    <input className="pay-input" type="number" min="0" step="1" placeholder="0" value={form.amount || ''} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
                  </label>
                  <label className="pay-field">
                    <span className="pay-field-lbl">Datum</span>
                    <input className="pay-input" type="date" value={form.date || ''} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
                  </label>
                </div>
                <label className="pay-field">
                  <span className="pay-field-lbl">Notiz (optional)</span>
                  <input className="pay-input" type="text" placeholder="z. B. Bonus, Sonderprovision …" value={form.note || ''} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
                </label>
              </div>
            )}

            <div className="pay-modal-actions">
              <button type="button" className="pay-btn pay-btn-ghost" onClick={closeModal} disabled={saving}>Abbrechen</button>
              <button type="button" className="pay-btn pay-btn-primary" onClick={modal === 'payment' ? saveManualPayment : saveManualPayout} disabled={saving}>
                {saving ? 'Speichern …' : 'Hinzufügen'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Zahlung bestätigen bzw. Rechnung nachreichen – der Zwischenschritt
          zwischen dem Status-Klick und dem abgehakten Posten. Eingänge legen die
          Rechnung an den Kunden ab, Auszahlungen die des Freelancers. */}
      {invModal && (() => {
        const { kind, item, mode } = invModal;
        const isPayment = kind === 'payment';
        const attachOnly = mode === 'attach';
        const oneTime = item.type === 'onetime';
        const periodLbl = item.source === 'manual'
          ? (item.note || (isPayment ? 'Einzelzahlung' : 'Einzelauszahlung'))
          : oneTime ? (item.label || 'Einmalzahlung') : monthLabel(item.periodKey);
        const documented = !!invFile || !!invLink.trim();
        const title = attachOnly
          ? 'Rechnung dokumentieren'
          : mode === 'invoiced' ? 'Rechnung gestellt'
            : isPayment ? 'Zahlungseingang bestätigen' : 'Auszahlung bestätigen';
        const hint = attachOnly
          ? isPayment
            ? 'Rechnung, die dieser Kunde bekommen hat, nachträglich ablegen. Am Status ändert sich nichts.'
            : 'Rechnung des Freelancers zu einer bereits getätigten Auszahlung nachreichen. Am Status ändert sich nichts.'
          : isPayment
            ? mode === 'invoiced'
              ? 'Lege die Rechnung ab, die du dem Kunden geschickt hast. Danach gilt sie als gestellt – mit Beleg im System.'
              : 'Der Kunde hat bezahlt. Hier landet die Rechnung, die du ihm geschickt hast, damit der Beleg bei der Zahlung liegt.'
            : 'Hänge die Rechnung an, die dir der Freelancer für diese Provision gestellt hat. Danach gilt der Posten als ausgezahlt – mit Beleg abgelegt.';
        const confirmLbl = attachOnly
          ? 'Speichern'
          : documented
            ? mode === 'invoiced' ? 'Gestellt & ablegen' : 'Bestätigen & ablegen'
            : mode === 'invoiced' ? 'Ohne Beleg stellen' : 'Ohne Rechnung bestätigen';
        const targetName = isPayment ? (item.companyName || 'Rechnung') : item.memberName;
        const targetMeta = isPayment
          ? `${typeof item.invoiceNo === 'number' ? `${fmtInvoiceNo(item.invoiceNo)} · ` : ''}${periodLbl}`
          : `${item.companyName ? `${item.companyName} · ` : ''}${periodLbl}`;
        return (
          <div className="pay-modal-overlay" onMouseDown={closeInvModal}>
            <motion.div
              className="pay-modal"
              onMouseDown={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
            >
              <div className="pay-modal-head">
                <h2>{title}</h2>
                <button type="button" className="pay-modal-close" onClick={closeInvModal} aria-label="Schließen" disabled={invBusy}>✕</button>
              </div>
              <div className="pay-modal-body">
                <div className="pay-cancel-target">
                  <span className="pay-cancel-target-name">
                    {targetName}
                    <span className="pay-inv-target-meta">{targetMeta}</span>
                  </span>
                  <span className="pay-cancel-target-amount">{isPayment ? '' : '−'}{fmtEur(item.amount)}</span>
                </div>
                <p className="pay-cancel-hint">{hint}</p>

                {invFile ? (
                  <div className="pay-inv-file">
                    <span aria-hidden="true">📄</span>
                    <a className="pay-inv-file-name" href={invFile.url} target="_blank" rel="noreferrer" title="Rechnung öffnen">
                      {invFile.name || 'Rechnung'}
                    </a>
                    {typeof invFile.size === 'number' && <span className="pay-inv-file-size">{fmtFileSize(invFile.size)}</span>}
                    <button type="button" className="pay-inv-file-act" onClick={() => invInputRef.current && invInputRef.current.click()} disabled={invBusy}>ersetzen</button>
                    <button type="button" className="pay-inv-file-act pay-inv-file-del" onClick={removeInvFile} disabled={invBusy} title="Datei entfernen">✕</button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="pay-inv-upload"
                    onClick={() => invInputRef.current && invInputRef.current.click()}
                    disabled={invBusy}
                  >
                    <span aria-hidden="true">⬆</span> {invBusy ? `Lädt hoch … ${invPct}%` : `${isPayment ? 'Gestellte Rechnung' : 'Rechnung'} hochladen (PDF, Bild …)`}
                  </button>
                )}
                <input
                  ref={invInputRef}
                  type="file"
                  className="pay-inv-file-input"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,image/*,application/pdf"
                  onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; uploadInvFile(f); }}
                />
                {invError && <p className="pay-inv-error">{invError}</p>}

                {!invFile && (
                  <label className="pay-field">
                    <span className="pay-field-lbl">oder Link zur Rechnung</span>
                    <input
                      className="pay-input"
                      type="url"
                      placeholder="https:// … (Drive, Dropbox, Notion …)"
                      value={invLink}
                      onChange={(e) => setInvLink(e.target.value)}
                    />
                  </label>
                )}
                {/* Eingänge tragen ihre eigene, fortlaufende Nummer schon auf dem
                    Datensatz – hier geht es um die Nummer des externen Dokuments. */}
                {!isPayment && (
                  <label className="pay-field">
                    <span className="pay-field-lbl">Rechnungsnummer (optional)</span>
                    <input
                      className="pay-input"
                      type="text"
                      placeholder="z. B. 2026-014"
                      value={invNo}
                      onChange={(e) => setInvNo(e.target.value)}
                    />
                  </label>
                )}
              </div>
              <div className="pay-modal-actions">
                <button type="button" className="pay-btn pay-btn-ghost" onClick={closeInvModal} disabled={invBusy}>Abbrechen</button>
                <button
                  type="button"
                  className={`pay-btn ${mode === 'invoiced' ? 'pay-btn-primary' : 'pay-btn-green'}`}
                  onClick={confirmInvModal}
                  disabled={invBusy || (attachOnly && !documented && !invoiceDoc(item))}
                >
                  {confirmLbl}
                </button>
              </div>
            </motion.div>
          </div>
        );
      })()}

      {cancelModal && (() => {
        const { kind, item, scope } = cancelModal;
        const isFuture = scope === 'future';
        const title = isFuture ? 'Zukünftige Rechnungen pausieren' : kind === 'payment' ? 'Rechnung stornieren' : 'Auszahlung stornieren';
        const targetName = kind === 'payment' ? (item.companyName || 'Rechnung') : (item.memberName || 'Auszahlung');
        const reasons = CANCEL_REASONS[isFuture ? 'future' : kind] || [];
        const ready = !!cancelReason.trim();
        return (
          <div className="pay-modal-overlay" onMouseDown={closeCancel}>
            <motion.div
              className="pay-modal"
              onMouseDown={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
            >
              <div className="pay-modal-head">
                <h2>{title}</h2>
                <button type="button" className="pay-modal-close" onClick={closeCancel} aria-label="Schließen">✕</button>
              </div>
              <div className="pay-modal-body">
                <div className="pay-cancel-target">
                  <span className="pay-cancel-target-name">{targetName}</span>
                  <span className="pay-cancel-target-amount">{kind === 'payout' ? '−' : ''}{fmtEur(item.amount)}</span>
                </div>
                {isFuture && (
                  <p className="pay-cancel-hint">Pausiert die monatliche Abrechnung dieses Kunden – es werden keine neuen Rechnungen (und Auszahlungen) mehr erzeugt, bis du wieder aktivierst. Bereits gestellte Rechnungen bleiben unverändert.</p>
                )}
                {reasons.length > 0 && (
                  <div className="pay-cancel-chips">
                    {reasons.map((r) => (
                      <button key={r} type="button" className={`pay-cancel-chip ${cancelReason === r ? 'pay-cancel-chip-on' : ''}`} onClick={() => setCancelReason(r)}>{r}</button>
                    ))}
                  </div>
                )}
                <label className="pay-field">
                  <span className="pay-field-lbl">Grund (Pflicht)</span>
                  <textarea
                    className="pay-input pay-textarea"
                    rows={3}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="z. B. Kunde hat nicht gezahlt, Rechnung erlassen …"
                  />
                </label>
              </div>
              <div className="pay-modal-actions">
                <button type="button" className="pay-btn pay-btn-ghost" onClick={closeCancel}>Abbrechen</button>
                <button type="button" className="pay-btn pay-btn-danger" onClick={confirmCancel} disabled={!ready}>
                  {isFuture ? 'Pausieren' : 'Stornieren'}
                </button>
              </div>
            </motion.div>
          </div>
        );
      })()}
    </div>
  );
};

export default FinanceDashboard;
